import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import DashboardLayout from './DashboardLayout';
import { SearchableSelect } from './SearchableSelect';
import { FaEye } from 'react-icons/fa';
import { MdEdit } from 'react-icons/md';
import { FilterToggleButton } from './FilterToggleButton';
import {
  useGetNatureOfComplaintsQuery,
  useUpdateNatureOfComplaintStatusMutation,
  useGetAccountableDepartmentsQuery,
  useGetActiveCategoriesQuery,
  useGetActiveSubcategoriesByCategoryQuery,
} from '../../redux/complaintApi';
import { useAuth } from '../../context/AuthContext';
import TablePagination from './TablePagination';
import { ApiLoadState, ApiLoadTableRow } from './ApiLoadState';
import { DEFAULT_PAGE_SIZE, getServerPagination } from '../../utils/pagination';
import {
  EMPTY_ISSUE_TYPE_LIST_FILTERS,
  getInitialIssueTypeListState,
  issueTypeListPath,
  issueTypeListStateToSearchParams,
} from '../../utils/issueTypeListState';

const EMPTY_FILTERS = EMPTY_ISSUE_TYPE_LIST_FILTERS;

const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

function AdminNatureOfComplaintList() {
  const { canAction } = useAuth();
  const canView = canAction('issue_types', 'view');
  const canCreate = canAction('issue_types', 'create');
  const canEdit = canAction('issue_types', 'edit');
  const canDelete = canAction('issue_types', 'delete');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const [initialListState] = useState(getInitialIssueTypeListState);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState(initialListState.filters);
  const [filterDraft, setFilterDraft] = useState(initialListState.filters);
  const listReturnState = useMemo(
    () => ({ from: issueTypeListPath({ filters }) }),
    [filters]
  );
  const pageSize = DEFAULT_PAGE_SIZE;
  const listQuery = useMemo(() => {
    const params = { page, per_page: pageSize };
    const q = search.trim();
    if (q) params.search = q;
    if (filters.accountable_department_id) {
      params.accountable_department_id = filters.accountable_department_id;
    }
    if (filters.category_id) params.category_id = filters.category_id;
    if (filters.subcategory_id) params.subcategory_id = filters.subcategory_id;
    if (filters.status === 'active') params.status = true;
    else if (filters.status === 'inactive') params.status = false;
    return params;
  }, [page, pageSize, search, filters]);
  const { data: natureRes, isFetching, isLoading, error, refetch } =
    useGetNatureOfComplaintsQuery(listQuery);
  const [viewRow, setViewRow] = useState(null);
  const [updateNatureOfComplaintStatus, { isLoading: isUpdatingStatus }] =
    useUpdateNatureOfComplaintStatusMutation();
  const [rows, setRows] = useState([]);

  const { data: departmentsRes } = useGetAccountableDepartmentsQuery(undefined, {
    skip: !isFilterOpen,
  });
  const { data: categoriesRes } = useGetActiveCategoriesQuery(
    { accountable_department_id: filterDraft.accountable_department_id },
    { skip: !isFilterOpen || !filterDraft.accountable_department_id }
  );
  const { data: subcategoriesRes } = useGetActiveSubcategoriesByCategoryQuery(
    filterDraft.category_id,
    { skip: !isFilterOpen || !filterDraft.category_id }
  );

  const departmentOptions = useMemo(() => {
    const list = departmentsRes?.data || departmentsRes || [];
    return (Array.isArray(list) ? list : []).map((dept) => ({
      value: String(dept.id),
      label: dept.name ?? String(dept.id),
    }));
  }, [departmentsRes]);

  const categoryOptions = useMemo(() => {
    const list = categoriesRes?.data || categoriesRes || [];
    return (Array.isArray(list) ? list : []).map((cat) => ({
      value: String(cat.id),
      label: cat.name ?? String(cat.id),
    }));
  }, [categoriesRes]);

  const subcategoryOptions = useMemo(() => {
    const list = subcategoriesRes?.data || subcategoriesRes || [];
    return (Array.isArray(list) ? list : []).map((sub) => ({
      value: String(sub.id),
      label: sub.name ?? String(sub.id),
    }));
  }, [subcategoriesRes]);

  useEffect(() => {
    if (!isFilterOpen) return;
    setFilterDraft(filters);
  }, [isFilterOpen, filters]);

  const hasActiveFilters = Boolean(
    filters.accountable_department_id ||
      filters.category_id ||
      filters.subcategory_id ||
      (filters.status && filters.status !== 'all')
  );

  useEffect(() => {
    const next = issueTypeListStateToSearchParams({ filters });
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [filters, searchParams, setSearchParams]);

  const handleFilterDraftChange = (field) => (event) => {
    const value = event?.target?.value ?? '';
    setFilterDraft((prev) => {
      if (field === 'accountable_department_id') {
        return {
          ...prev,
          accountable_department_id: value,
          category_id: '',
          subcategory_id: '',
        };
      }
      if (field === 'category_id') {
        return {
          ...prev,
          category_id: value,
          subcategory_id: '',
        };
      }
      return { ...prev, [field]: value };
    });
  };

  const applyFilters = () => {
    setFilters(filterDraft);
    setPage(1);
  };

  const resetFilters = () => {
    setFilterDraft(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
    setPage(1);
    setIsFilterOpen(false);
  };

  useEffect(() => {
    const list = natureRes?.data || natureRes || [];
    const safe = Array.isArray(list) ? list : [];
    const mapped = safe.map((r, index) => {
      const status = r.status ?? true;
      return {
        id: r.id ?? r.issue_type_id ?? `row-${index}`,
        name: r.name || r.complaint_type || '',
        department: r.department_name || r.accountable_department_name || '',
        category: r.category_name || '',
        subcategory: r.subcategory_name || r.sub_category_name || '',
        active: status === true,
      };
    });

    setRows(mapped);
  }, [natureRes]);

  const { currentPage, totalCount, pageSize: effectivePageSize } = getServerPagination(
    natureRes,
    { page, pageSize }
  );
  const pageItems = rows;

  const toggleActive = (r) => {
    if (isUpdatingStatus) return;
    const nextStatus = !(r.active ?? false);
    updateNatureOfComplaintStatus({
      id: Number(r.id),
      status: nextStatus,
    })
      .unwrap()
      .then(() => toast.success('Status updated successfully.'))
      .catch((err) =>
        toast.error(err?.data?.message || 'Failed to update status.')
      );
  };

  return (
    <DashboardLayout title="Issue Type">
      <div className="space-y-4 py-4">
        <div className="rounded-2xl bg-white p-4 shadow-sm sm:p-6">
          <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="relative w-full min-w-0 md:max-w-xs">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </span>
              <input
                type="text"
                placeholder="Search Issue Type"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="w-full rounded-lg border border-gray-200 bg-[#faf8f5] py-2 pl-10 pr-3 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              />
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              <FilterToggleButton
                active={hasActiveFilters}
                onClick={() => setIsFilterOpen((prev) => !prev)}
                ariaLabel="Toggle issue type filters"
              />
              {canCreate ? (
                <Link
                  to="/dashboard/admin/issue-type/add"
                  state={listReturnState}
                  className="inline-flex items-center justify-center rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white shadow-sm"
                >
                  + Add Issue Type
                </Link>
              ) : null}
            </div>
          </div>

          {isFilterOpen ? (
            <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
              <label className="flex flex-col text-sm text-gray-700">
                Accountable Department
                <SearchableSelect
                  name="accountable_department_id"
                  value={filterDraft.accountable_department_id}
                  onChange={handleFilterDraftChange('accountable_department_id')}
                  options={departmentOptions}
                  placeholder="All Departments"
                  clearable
                  className="mt-1 rounded-lg border border-gray-200 bg-[#faf8f5] px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                />
              </label>
              <label className="flex flex-col text-sm text-gray-700">
                Category
                <SearchableSelect
                  name="category_id"
                  value={filterDraft.category_id}
                  onChange={handleFilterDraftChange('category_id')}
                  options={categoryOptions}
                  placeholder="All Categories"
                  clearable
                  disabled={!filterDraft.accountable_department_id}
                  className="mt-1 rounded-lg border border-gray-200 bg-[#faf8f5] px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand disabled:cursor-not-allowed disabled:opacity-60"
                />
              </label>
              <label className="flex flex-col text-sm text-gray-700">
                Sub Category
                <SearchableSelect
                  name="subcategory_id"
                  value={filterDraft.subcategory_id}
                  onChange={handleFilterDraftChange('subcategory_id')}
                  options={subcategoryOptions}
                  placeholder="All Sub Categories"
                  clearable
                  disabled={!filterDraft.category_id}
                  className="mt-1 rounded-lg border border-gray-200 bg-[#faf8f5] px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand disabled:cursor-not-allowed disabled:opacity-60"
                />
              </label>
              <label className="flex flex-col text-sm text-gray-700">
                Status
                <SearchableSelect
                  name="status"
                  value={filterDraft.status}
                  onChange={handleFilterDraftChange('status')}
                  options={STATUS_FILTER_OPTIONS}
                  placeholder="All"
                  className="mt-1 rounded-lg border border-gray-200 bg-[#faf8f5] px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                />
              </label>
              <div className="flex items-end gap-2 sm:col-span-2">
                <button
                  type="button"
                  onClick={applyFilters}
                  className="inline-flex w-full items-center justify-center rounded-lg bg-brand px-3 py-2 text-sm font-semibold text-white shadow-sm"
                >
                  Search
                </button>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="inline-flex w-full items-center justify-center rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Reset
                </button>
              </div>
            </div>
          ) : null}

          <ApiLoadState
            isLoading={isLoading}
            isFetching={isFetching}
            hasData={rows.length > 0}
            error={error}
            refetch={refetch}
            message="Loading Issue Types…"
            className="py-6 md:hidden"
          />
          {!error && (rows.length > 0 || (!isLoading && !isFetching)) && pageItems.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-500 md:hidden">No records found.</p>
          ) : !error && (rows.length > 0 || (!isLoading && !isFetching)) && pageItems.length > 0 ? (
            <ul className="mb-4 space-y-3 md:hidden">
              {pageItems.map((r, idx) => (
                <li
                  key={r.id}
                  className="rounded-lg border border-gray-100 bg-[#faf8f5] p-3"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-gray-500">
                        #{(currentPage - 1) * pageSize + idx + 1}
                      </p>
                      <p className="mt-1 text-sm font-semibold leading-snug text-gray-900">
                        {r.name || '—'}
                      </p>
                      <p className="mt-2 text-xs text-gray-600">
                        Dept:{' '}
                        <span className="font-medium text-gray-800">{r.department || '—'}</span>
                      </p>
                      <p className="mt-1 text-xs text-gray-600">
                        Category:{' '}
                        <span className="font-medium text-gray-800">{r.category || '—'}</span>
                      </p>
                      <p className="mt-1 text-xs text-gray-600">
                        Sub Category:{' '}
                        <span className="font-medium text-gray-800">{r.subcategory || '—'}</span>
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      {canView ? (
                        <button
                          type="button"
                          onClick={() => setViewRow(r)}
                          className="rounded-lg p-1.5 text-red-700 hover:bg-white"
                          aria-label="View Issue Type"
                        >
                          <FaEye className="h-5 w-5" />
                        </button>
                      ) : null}
                      {canEdit && r.id != null && !String(r.id).startsWith('row-') ? (
                        <Link
                          to={`/dashboard/admin/issue-type/edit/${r.id}`}
                          state={listReturnState}
                          className="rounded-lg p-1.5 text-red-700 hover:bg-white"
                          aria-label="Edit Issue Type"
                        >
                          <MdEdit className="h-5 w-5" />
                        </Link>
                      ) : null}
                    </div>
                  </div>
                  {canDelete && r.id != null && !String(r.id).startsWith('row-') ? (
                    <div className="mt-3 flex items-center justify-between border-t border-gray-200/80 pt-3">
                      <span className="text-xs text-gray-500">
                        {r.active ? 'Active' : 'Inactive'}
                      </span>
                      <button
                        type="button"
                        disabled={isUpdatingStatus}
                        onClick={() => toggleActive(r)}
                        aria-label={
                          r.active ? 'Disable issue type' : 'Enable issue type'
                        }
                        className={`inline-flex h-5 w-10 items-center rounded-full border px-[2px] transition disabled:opacity-50 ${
                          r.active
                            ? 'border-emerald-600 bg-[#008E36]'
                            : 'border-gray-300 bg-gray-300'
                        }`}
                      >
                        <span
                          className={`h-4 w-4 rounded-full bg-white shadow transition-transform ${
                            r.active ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}

          <div className="hidden overflow-x-auto md:block">
            <table className="min-w-full border-collapse text-sm">
              <thead>
                <tr className="">
                  <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                    Sr. No.
                  </th>
                  <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                    Accountable Department
                  </th>
                  <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                    Category
                  </th>
                  <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                    Sub Category
                  </th>
                  <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                    Name
                  </th>
                  <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                <ApiLoadTableRow
                  colSpan={6}
                  isLoading={isLoading}
                  isFetching={isFetching}
                  hasData={rows.length > 0}
                  error={error}
                  refetch={refetch}
                  message="Loading issue types..."
                />
                {!error && (rows.length > 0 || (!isLoading && !isFetching)) &&
                  pageItems.map((r, idx) => (
                  <tr key={r.id} className="hover:bg-amber-50/40">
                    <td className="border-b border-gray-100 px-3 py-2.5 text-gray-700">
                      {(currentPage - 1) * pageSize + idx + 1}
                    </td>
                    <td className="border-b border-gray-100 px-3 py-2.5 text-gray-700">
                      {r.department || '—'}
                    </td>
                    <td className="border-b border-gray-100 px-3 py-2.5 text-gray-700">
                      {r.category || '—'}
                    </td>
                    <td className="border-b border-gray-100 px-3 py-2.5 text-gray-700">
                      {r.subcategory || '—'}
                    </td>
                    <td className="border-b border-gray-100 px-3 py-2.5 text-gray-700">
                      {r.name || '—'}
                    </td>
                    <td className="border-b border-gray-100 px-3 py-2.5">
                      <div className="flex items-center gap-3">
                        {canView ? (
                          <button
                            type="button"
                            onClick={() => setViewRow(r)}
                            className="rounded-md p-1 text-red-700 hover:bg-amber-50/60 hover:text-red-800"
                            aria-label="View issue type"
                            title="View"
                          >
                            <FaEye className="h-5 w-5 text-red-700" />
                          </button>
                        ) : null}
                        {canEdit && r.id != null && !String(r.id).startsWith('row-') ? (
                          <Link
                            to={`/dashboard/admin/issue-type/edit/${r.id}`}
                            state={listReturnState}
                            className="rounded-md p-1 text-red-700 hover:bg-amber-50/60 hover:text-red-800"
                            aria-label="Edit Issue Type"
                            title="Edit"
                          >
                            <MdEdit className="h-5 w-5 text-red-700" />
                          </Link>
                        ) : null}
                        {canDelete && r.id != null && !String(r.id).startsWith('row-') ? (
                        <button
                          type="button"
                          disabled={isUpdatingStatus}
                          onClick={() => toggleActive(r)}
                          aria-label={
                            r.active ? 'Disable issue type' : 'Enable issue type'
                          }
                          className={`inline-flex h-5 w-10 items-center rounded-full border px-[2px] transition disabled:opacity-50 ${
                            r.active
                              ? 'border-emerald-600 bg-[#008E36]'
                              : 'border-gray-300 bg-gray-300'
                          }`}
                        >
                          <span
                            className={`h-4 w-4 rounded-full bg-white shadow transition-transform ${
                              r.active ? 'translate-x-5' : 'translate-x-0'
                            }`}
                          />
                        </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
                {!error && !isLoading && !isFetching && pageItems.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-3 py-8 text-center text-sm text-gray-500"
                    >
                      No records found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <TablePagination
            totalCount={totalCount}
            currentPage={currentPage}
            onPageChange={setPage}
            pageSize={effectivePageSize}
          />
        </div>
      </div>
    

      {viewRow && (
        <div
          className="fixed inset-0 z-40 flex justify-end bg-black/30"
          onClick={() => setViewRow(null)}
          role="presentation"
        >
          <div
            className="flex h-full w-full min-w-0 max-w-full flex-col bg-white shadow-2xl sm:max-w-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-100 px-4 py-4 sm:px-6">
              <h2 className="text-lg font-semibold text-brand">Issue Type</h2>
              <button
                type="button"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-2xl leading-none text-brand hover:bg-gray-100"
                onClick={() => setViewRow(null)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="min-h-0 flex-1 space-y-2 overflow-y-auto px-4 py-4 pb-10 sm:px-6 sm:pb-8">
              {[
                { label: 'Accountable Department', value: viewRow.department || '—' },
                { label: 'Category', value: viewRow.category || '—' },
                { label: 'Sub Category', value: viewRow.subcategory || '—' },
                { label: 'Name', value: viewRow.name || '—' },
                { label: 'Status', value: viewRow.active ? 'Active' : 'Inactive' },
              ].map((field) => (
                <div
                  key={field.label}
                  className="rounded-lg border border-gray-100 bg-[#faf8f5] px-3 py-2.5"
                >
                  <p className="text-xs text-gray-500">{field.label}</p>
                  <p className="mt-0.5 text-sm font-medium text-gray-900">{field.value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

export default AdminNatureOfComplaintList;

